import { Sema } from "async-sema";
import { getLogger } from "../../../api/getServices";
import { getCurrentEditorStatus } from "../../redux/selectors/current";
import { getSourceType } from "../../redux/selectors/session";
import { getStore } from "../../redux/store/appStore";
import { EditorStatus } from "../../types/EditorStatus";
import { makeSaveDraft } from "./saveDraft";

/**
 * Wrapper function used to prevent two concurrent save draft requests.
 *
 * Saving a draft requires a sequence number with every request. The response
 * to a request includes the next sequence number to be used. This means we may
 * not have multiple "save draft" requests inflight.
 *
 * #### Queue Logic ####
 * It's common to have "save draft" calls made while there is a request
 * in flight. This function queued the request and will wait until original
 * request is complete before executing the queued request.
 *
 * Only one additional request is queued. If a third (or more) requests are
 * made while the first request is inflight, this function will behave as if
 * all intermediate requests were dropped and only the very last (most recent)
 * will be executed.
 *
 * The following diagram illustrates this function's behavior.
 *
 *
 * # One request made while first request is in-flight.
 *  _________________________________________________________________________
 * | save_draft_1 |------------------------------------|                     |
 * |_________________________________________________________________________|
 *  _________________________________________________________________________
 * | save_draft_1 |------------------------------------|                     |
 * |                        save_draft_2 (queued)      |-------------------| |
 * |_________________________________________________________________________|
 *
 *
 *  # Several request made while first request is in-flight.
 *  _________________________________________________________________________
 * | save_draft_1 |----------------------------------------|                 |
 * |_________________________________________________________________________|
 *  _________________________________________________________________________
 * | save_draft_1 |----------------------------------------|                 |
 * |                 save_draft_2 (queued)                 |------|          |
 * |_________________________________________________________________________|
 *  _________________________________________________________________________
 * | save_draft_1 |----------------------------------------|                 |
 * |                 save_draft_2 (dropped)                                  |
 * |                     save_draft_3 (new queued)         |-------|         |
 * |_________________________________________________________________________|
 *  _________________________________________________________________________
 * | save_draft_1 |----------------------------------------|                 |
 * |                 save_draft_2 (dropped)                                  |
 * |                     save_draft_3 (dropped)                              |
 * |                          save_draft_4 (new queued)    |---------|       |
 * |_________________________________________________________________________|
 */
export const makeSequentialSaveDraftWithQueue = () => {
  const saveDraft = makeSaveDraft();

  let inflight = false;
  let queued = false;

  return async () => {
    const { getState } = getStore();

    // Ignore auto save requests while editor is queued.
    if (getCurrentEditorStatus(getState()) === EditorStatus.QUEUED) {
      return;
    }

    if (inflight) {
      queued = true;
      return;
    }

    do {
      inflight = true;
      queued = false;

      try {
        await draftSequenceMutex.acquire();
        await saveDraft();
      } catch (error) {
        getLogger().report("kernels", "notebook_error", {
          eventName: "error",
          editor: getSourceType(getState()),
          errorCode: "save_draft",
          message: error?.message || "Save draft failed with NULL error.",
        });
      } finally {
        draftSequenceMutex.release();
        inflight = false;
      }
    } while (queued);
  };
};

// Provide a mutex for any operations that modify a draft's sequence number.
export const draftSequenceMutex = new Sema(1);
